import React from 'react';
import type { AccountabilityFlags } from '../SpineScore';

const FLAG_LABELS: Record<keyof AccountabilityFlags, string> = {
  conflict_of_interest: 'Conflict of interest',
  flip_flop: 'Reversed position',
  corruption_investigation: 'Corruption investigation',
};

/** Flags below this probability aren't shown — same threshold used on the Spine Score gauge. */
const FLAG_DISPLAY_THRESHOLD = 0.6;

/**
 * Renders real accountability flags (from Jev's noul questions) as case-file
 * exhibit tags. Returns null when there's nothing to show — callers should
 * conditionally render a "no flags" state themselves if needed.
 */
export const RedFlagBadges: React.FC<{
  flags?: AccountabilityFlags | null;
  className?: string;
}> = ({ flags, className }) => {
  if (!flags) return null;
  const active = (Object.keys(FLAG_LABELS) as (keyof AccountabilityFlags)[]).filter(
    (key) => flags[key] >= FLAG_DISPLAY_THRESHOLD,
  );
  if (active.length === 0) return null;

  return (
    <div className={`flex gap-1.5 flex-wrap ${className ?? ''}`}>
      {active.map((key) => (
        <span
          key={key}
          className="font-bold text-[9px] bg-cream-200 dark:bg-navy-700 border border-cream-300 dark:border-navy-600 text-navy-700 dark:text-cream-200 px-2 py-0.5 uppercase tracking-widest"
        >
          {FLAG_LABELS[key]}
        </span>
      ))}
    </div>
  );
};

/** Count of active flags, for header pills like "3 red flags". */
export function countActiveFlags(flags?: AccountabilityFlags | null): number {
  if (!flags) return 0;
  return (Object.keys(FLAG_LABELS) as (keyof AccountabilityFlags)[]).filter(
    (key) => flags[key] >= FLAG_DISPLAY_THRESHOLD,
  ).length;
}
